import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import InstallPrompt from "@/components/InstallPrompt";
import NotificationsOptIn from "@/components/NotificationsOptIn";
import { AuthProvider } from "@/contexts/AuthContext";
import { I18nProvider } from "@/i18n/I18nContext";
import { captureAttribution, pageview } from "@/lib/analytics";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Feed from "@/pages/Feed";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Discover from "@/pages/Discover";
import ProductDetail from "@/pages/ProductDetail";
import SellerDashboard from "@/pages/SellerDashboard";
import ProductCreate from "@/pages/ProductCreate";
import AdminDashboard from "@/pages/AdminDashboard";
import Billing from "@/pages/Billing";
import PaymentCallback from "@/pages/PaymentCallback";
import AuthCallback from "@/pages/AuthCallback";
import SelectRole from "@/pages/SelectRole";
import FoundersWelcome from "@/pages/FoundersWelcome";
import FoundersWall from "@/pages/FoundersWall";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Referrals from "@/pages/Referrals";
import Launch from "@/pages/Launch";
import Legal from "@/pages/Legal";
import PartnerApply from "@/pages/PartnerApply";
import Orders from "@/pages/Orders";
import Payouts from "@/pages/Payouts";
import Earnings from "@/pages/Earnings";
import Privacy from "@/pages/Privacy";
import BuyerProtection from "@/pages/BuyerProtection";
import LegalCenter from "@/pages/legal/LegalCenter";
import Terms from "@/pages/legal/Terms";
import SellerAgreement from "@/pages/legal/SellerAgreement";
import StageTerms from "@/pages/legal/StageTerms";
import RefundPolicy from "@/pages/legal/RefundPolicy";
import CommunityGuidelines from "@/pages/legal/CommunityGuidelines";
import IPPolicy from "@/pages/legal/IPPolicy";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import Trademarks from "@/pages/legal/Trademarks";
import GetApp from "@/pages/GetApp";
import Join from "@/pages/Join";
import AdvertScenes from "@/pages/AdvertScenes";
import Watch from "@/pages/Watch";
import Briefing from "@/pages/Briefing";
import { CreatorLicenceGate } from "@/components/legal/CreatorLicenceGate";
import AccountPrivacy from "@/pages/AccountPrivacy";
import Wanted from "@/pages/Wanted";
import OpenDemands from "@/pages/OpenDemands";
import ClaimWant from "@/pages/ClaimWant";
import BulkUpload from "@/pages/BulkUpload";
import ForgotPassword, { ResetPassword } from "@/pages/ForgotPassword";
import Store from "@/pages/Store";
import StageStudio from "@/pages/StageStudio";
import LiveHost from "@/pages/LiveHost";
import Stages from "@/pages/Stages";
import BuyerFinder from "@/pages/BuyerFinder";
import SellerSocial from "@/pages/SellerSocial";
import CookieNotice from "@/components/CookieNotice";
import { SocialProofStrip } from "@/components/SocialProofStrip";
import "@/App.css";

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. We synchronously detect #session_id in the URL
 * fragment during render (BEFORE React Router matches) to avoid the
 * ProtectedRoute race condition documented in the Emergent Auth playbook.
 */
function AppRouter() {
  const loc = useLocation();
  useEffect(() => {
    captureAttribution();
  }, []);
  useEffect(() => {
    pageview();
  }, [loc.pathname]);
  if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Feed />} />
      <Route path="/f/:productId" element={<Feed />} />
      <Route path="/welcome" element={<Landing />} />
      <Route path="/launch" element={<Launch />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/buyer-protection" element={<BuyerProtection />} />
      <Route path="/legal-center" element={<LegalCenter />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/seller-agreement" element={<SellerAgreement />} />
      <Route path="/stage-terms" element={<StageTerms />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/community-guidelines" element={<CommunityGuidelines />} />
      <Route path="/ip-policy" element={<IPPolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/trademarks" element={<Trademarks />} />
      <Route path="/app" element={<GetApp />} />
      <Route path="/join" element={<Join />} />
      <Route path="/join/scenes" element={<AdvertScenes />} />
      <Route path="/watch" element={<Watch />} />
      <Route path="/briefing" element={<Briefing />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/account" element={
        <ProtectedRoute>
          <AccountPrivacy />
        </ProtectedRoute>
      } />
      <Route path="/partners" element={<PartnerApply />} />
      <Route path="/founders" element={<FoundersWall />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/discover" element={<Discover />} />
      <Route path="/open-demands" element={<OpenDemands />} />
      <Route path="/claim" element={<ClaimWant />} />
      <Route path="/wanted" element={
        <ProtectedRoute>
          <Wanted />
        </ProtectedRoute>
      } />
      <Route path="/product/:id" element={<ProductDetail />} />
      <Route path="/store/:sellerId" element={<Store />} />
      <Route path="/stages" element={<Stages />} />
      <Route path="/seller/social" element={
        <ProtectedRoute roles={["seller", "admin"]}>
          <SellerSocial />
        </ProtectedRoute>
      } />
      <Route path="/seller/buyers" element={
        <ProtectedRoute roles={["seller", "admin"]}>
          <BuyerFinder />
        </ProtectedRoute>
      } />
      <Route path="/seller/stage" element={
        <ProtectedRoute roles={["seller", "admin"]}>
          <CreatorLicenceGate><StageStudio /></CreatorLicenceGate>
        </ProtectedRoute>
      } />
      <Route path="/seller/live/:postId" element={
        <ProtectedRoute roles={["seller", "admin"]}>
          <LiveHost />
        </ProtectedRoute>
      } />
      <Route path="/orders" element={
        <ProtectedRoute>
          <Orders />
        </ProtectedRoute>
      } />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/select-role" element={
        <ProtectedRoute>
          <SelectRole />
        </ProtectedRoute>
      } />
      <Route path="/seller" element={
        <ProtectedRoute roles={["seller", "admin"]}>
          <SellerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/founders-welcome" element={
        <ProtectedRoute roles={["seller", "admin"]}>
          <FoundersWelcome />
        </ProtectedRoute>
      } />
      <Route path="/seller/new" element={
        <ProtectedRoute roles={["seller", "admin"]}>
          <ProductCreate />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={<Navigate to="/seller" replace />} />
      <Route path="/seller/bulk" element={
        <ProtectedRoute roles={["seller", "admin"]}>
          <BulkUpload />
        </ProtectedRoute>
      } />
      <Route path="/payouts" element={
        <ProtectedRoute roles={["seller", "admin"]}>
          <Payouts />
        </ProtectedRoute>
      } />
      <Route path="/earnings" element={
        <ProtectedRoute roles={["seller", "admin"]}>
          <Earnings />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute roles={["admin", "staff"]}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute>
          <Billing />
        </ProtectedRoute>
      } />
      <Route path="/referrals" element={
        <ProtectedRoute>
          <Referrals />
        </ProtectedRoute>
      } />
      <Route path="/payment/callback" element={
        <ProtectedRoute>
          <PaymentCallback />
        </ProtectedRoute>
      } />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <I18nProvider>
      <BrowserRouter>
        <Layout>
          <AppRouter />
        </Layout>
        <Toaster />
        <InstallPrompt />
        <CookieNotice />
        <SocialProofStrip />
        <NotificationsOptIn />
      </BrowserRouter>
      </I18nProvider>
    </AuthProvider>
  );
}
